import {
  Component,
  OnDestroy,
  OnInit,
  Inject,
  Input,
  ChangeDetectorRef,
  DOCUMENT
} from '@angular/core';
import { Store } from '@ngrx/store';
import * as fromLocatorStore from 'src/app/locator-store';
import * as fromUIStore from 'src/app/core/store';
import { LocatorLofiQueryResult } from 'src/app/locator/models/locator-lofi-report';
import { Subscription } from 'rxjs/internal/Subscription';


import { tap } from 'rxjs/operators';
import { LocatorPowerBiReportingInfo } from 'src/app/locator/models/locator-power-bi-reporting-info';
import { LocatorReportTypes } from 'src/app/locator/types/locator-report.types';
import { MapLayerStyleService } from 'src/app/shared/atlas-mapping/services/map-layer-style-service';
import * as fromCore from 'src/app/core/store';
import { AiPromptTemplateLibraryService } from 'src/app/core/services/ai-prompt-template-library.service';
import { DialogService } from 'src/app/core/services/dialog.service';
import { AiAssistantDialogComponent } from '../../../ai-assistant/ai-assistant-dialog/ai-assistant-dialog.component';
import { DialogWidth } from 'src/app/shared/atlas-dialog/enums/dialog-width.enum';
import { convertStatsDataToMarkDown } from 'src/app/shared/helpers/lofi-reports-helper';
import { isAiAssistantDialogOpen } from 'src/app/shared/utils/dialog-utils';
import { MatDialog } from '@angular/material/dialog';

@Component({
    selector: 'atlas-locator-reports-panel',
    templateUrl: './locator-reports-panel.component.html',
    styleUrls: ['./locator-reports-panel.component.less'],
    standalone: false
})
export class LocatorReportsPanelComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();

  @Input() pieGridView: [number, number] = [250, 620];
  @Input() numberCardView: [number, number] = [300, 620];
  @Input() graphHeight: number;
  titleType: string = '';

  isAiAssistantEnabled$ = this.locatorStore$.select(
    fromCore.isAiAssistantEnabled
  );

  identifier = this.getGUID();

  selectedQueryItem: LocatorLofiQueryResult | null =
    new LocatorLofiQueryResult();

  selectedShapes$ = this.locatorStore$
    .select(fromLocatorStore.getSelectedShapes)
    .pipe(tap(() => this.cdr.detectChanges()));

  selectedLocation$ = this.locatorStore$.select(
    fromLocatorStore.getSelectedLocation
  );

  isLofiReportAvailable$ = this.locatorStore$
    .select(fromLocatorStore.getIsLofiReportAvailable)
    .pipe(tap(() => this.cdr.detectChanges()));

  isReportDataAvailable$ = this.locatorStore$.select(
    fromLocatorStore.getIsReportDataAvailable
  );

  showSummaryStatsReport$ = this.UIStore$.select(
    fromUIStore.showSummaryStatsReport
  );

  queryTotal: number = -1;
  queryItems: LocatorLofiQueryResult[] = [];

  graphConfig: any = {};
  selectedShapeName: string;
  selectedLocationName: string;
  showStats: boolean;
  showGraph: boolean;
  shapesLength: number;
  showSummaryStatsReport: boolean;
  pbiReportList: LocatorPowerBiReportingInfo[] = [];
  summaryStatsReportAvailable: boolean;

  constructor(
    @Inject(DOCUMENT) private _document: HTMLDocument,
    private locatorStore$: Store<fromLocatorStore.State>,
    private UIStore$: Store<fromUIStore.State>,
    private cdr: ChangeDetectorRef,
    private layerStyleService: MapLayerStyleService,
    private dialogService: DialogService,
    private aiPromptService: AiPromptTemplateLibraryService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.selectedLocation$.subscribe((selectedLocation) => {
        if (selectedLocation) {
          this.selectedLocationName = selectedLocation.name;
        }
      })
    );
    this.subscription.add(
      this.selectedShapes$.subscribe((selectedShapes) => {
        if (
          selectedShapes &&
          selectedShapes.length > 0 &&
          selectedShapes[0].name
        ) {
          this.selectedShapeName = selectedShapes[0].name;
          this.shapesLength = selectedShapes.length;
        } else {
          this.shapesLength = 0;
        }
      })
    );

    this.subscription.add(
      this.locatorStore$
        .select(fromLocatorStore.getLofiReport)
        .subscribe((lofFiReport) => {
          if (lofFiReport) {
            this.queryItems = lofFiReport as LocatorLofiQueryResult[];
            this.queryTotal = this.queryItems.length;

            this.selectedQueryItem = this.queryItems[0];
            this.setGraphConfig(this.selectedQueryItem);
            this.updateTitleType();
          } else {
            this.queryTotal = 0;
            this.queryItems = [];
            this.selectedQueryItem = null;
          }
        })
    );

    this.subscription.add(
      this.locatorStore$
        .select(fromLocatorStore.getLocatorPowerBiReports)
        .subscribe((reports) => {
          this.pbiReportList = reports ? reports : [];
          this.summaryStatsReportAvailable = this.pbiReportList.some(
            (pbiReport) =>
              pbiReport.reportType === LocatorReportTypes.Summary_Stats
          );
        })
    );
    this.subscription.add(
      this.showSummaryStatsReport$.subscribe(
        (showSummaryStatsReport) =>
          (this.showSummaryStatsReport = showSummaryStatsReport)
      )
    );
  }

  onGraphClicked(evt: any) {
    if (evt.mapLayerId) {
      this.layerStyleService.toggleSpotlight(evt.mapLayerId, evt.name);
    }
  }

  private updateTitleType(): void {
    if (this.selectedQueryItem) {
      let title: string = '';
      if (this.selectedQueryItem.shapeName === this.selectedShapeName) {
        title = this.buildShapeTitles(this.selectedQueryItem);
      } else if (
        this.selectedQueryItem.shapeName === this.selectedLocationName
      ) {
        title = this.buildLocationTitles(this.selectedQueryItem);
      }
      this.titleType = this.selectedQueryItem.pageTitle
        ? `${title} - ${this.selectedQueryItem.pageTitle}`
        : title;
    }
  }

  private buildShapeTitles(queryItem: {
    shapeName: string;
    isStats: boolean;
    isGraph: boolean;
  }): string {
    if (queryItem.isStats) {
      return 'Catchment Stats';
    }
    return 'Catchment Graph';
  }

  private buildLocationTitles(queryItem: {
    shapeName: string;
    isStats: boolean;
    isGraph: boolean;
  }): string {
    if (queryItem.isStats) {
      return 'Location Stats';
    }
    return 'Location Graph';
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  selectQuery(position: number) {
    this.activateDot(position);
    this.selectedQueryItem = this.queryItems[position];
    this.updateTitleType();
    this.setGraphConfig(this.selectedQueryItem);
  }

  onAiAssistantClicked(): void {
    // build prompy from stats

    var prompt =
      'Write a clear, concise paragraph explaining the following data. Avoid jargon and be suitable for a non-technical audience. \n';
    prompt =
      prompt +
      '**' +
      this.selectedLocationName +
      ' - ' +
      this.selectedShapeName +
      '.** \n';
    prompt = prompt + convertStatsDataToMarkDown(this.selectedQueryItem);

    // add prompt to the service
    this.aiPromptService.setCurrentPromptText(prompt);

    if (!isAiAssistantDialogOpen(this.dialog)) {
      this.dialogService.show(AiAssistantDialogComponent, {
        width: DialogWidth.Large,
        panelClass: 'dialog-full-width-height',
        disableClose: true,
        hasBackdrop: false
      });
    }
  }

  private activateDot(postion: number) {
    const container = this._document.getElementById(this.identifier);

    var dots = container?.querySelectorAll('.dot');

    if (dots) {
      dots.forEach((dot) => {
        dot.className = dot.className.replace(' active', '');
      });

      dots[postion].className += ' active';
    }
  }

  private setGraphConfig(selectedQuery: LocatorLofiQueryResult) {
    this.graphConfig =
      selectedQuery === undefined ||
      selectedQuery.graphConfiguration == null ||
      selectedQuery.graphConfiguration == undefined
        ? {}
        : JSON.parse(selectedQuery.graphConfiguration);
  }

  private getGUID(): string {
    let d = new Date().getTime();
    const guid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      (c) => {
        const r = (d + Math.random() * 16) % 16 | 0;
        d = Math.floor(d / 16);
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
      }
    );
    return guid;
  }
}
